import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import { StopOptionType } from '@/src/types/filterType'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'

const useStopFilterManager = (journeyLeg: 0 | 1 = 0) => {
    const { filterData, selectedJourneyLeg } = useSearchResults(journeyLeg)
    const updateCache = useUpdateSearchResultCache()
    const t = useTranslations('common')
    const { triggerSrpEvent } = useClevertapForSrp()
    const title = t('stops', { s: 's' })
    const { NewStops } = filterData || {}
    const targetStops = NewStops?.[selectedJourneyLeg]
    const { selectedValues: selectedStops, options, prices } = targetStops || {}

    const getStopLabelFromStopNumber = useCallback(
        (stops: number) => {
            return stops === 0 ? t('direct') : `${stops} ${stops > 1 ? t('stops', { s: 's' }) : t('stops', { s: '' })}`
        },
        [t]
    )

    const stopOptions: StopOptionType[] | undefined = useMemo(() => {
        return options?.map((option, index) => {
            return {
                id: option,
                label: getStopLabelFromStopNumber(Number(option)),
                price: prices?.[index] || 0,
                isSelected: !!selectedStops?.includes(option)
            }
        })
    }, [options, prices, selectedStops, getStopLabelFromStopNumber])

    const isNonstopFlightAvailable = useMemo(() => {
        return Boolean(stopOptions?.some(option => option.id === 0))
    }, [stopOptions])

    const stopToggle = useCallback(
        (option: StopOptionType) => {
            const isRemoving = selectedStops?.includes(option.id)

            if (isRemoving) {
                updateCache(currentData => {
                    if (currentData?.filterData?.NewStops?.[selectedJourneyLeg]) {
                        currentData.filterData.NewStops[selectedJourneyLeg].selectedValues =
                            currentData.filterData.NewStops[selectedJourneyLeg].selectedValues.filter(
                                s => s !== option.id
                            )
                    }
                    return currentData as NonNullable<typeof currentData>
                })
            } else {
                updateCache(currentData => {
                    if (currentData?.filterData?.NewStops?.[selectedJourneyLeg]) {
                        currentData.filterData.NewStops[selectedJourneyLeg].selectedValues.push(option.id)
                    }
                    return currentData as NonNullable<typeof currentData>
                })
            }

            const selectedValues = isRemoving
                ? selectedStops?.filter(s => s !== option.id) || []
                : [...(selectedStops || []), option.id]

            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'stops',
                action_value: selectedValues.length > 0 ? selectedValues.join(',') : 'none'
            })
        },
        [selectedStops, updateCache, selectedJourneyLeg, triggerSrpEvent]
    )

    const stopReset = useCallback(() => {
        updateCache(currentData => {
            if (currentData?.filterData?.NewStops?.[selectedJourneyLeg]) {
                currentData.filterData.NewStops[selectedJourneyLeg].selectedValues = []
            }
            return currentData as NonNullable<typeof currentData>
        })

        triggerSrpEvent('Air_UI_Action', {
            action_name: 'filter',
            action_type: 'reset',
            action_value: 'stops'
        })
    }, [updateCache, selectedJourneyLeg, triggerSrpEvent])

    const nonStopChecked = useMemo(() => {
        return selectedStops?.length === 1 && selectedStops.includes(0)
    }, [selectedStops, selectedJourneyLeg])

    const nonStopToggle = useCallback(() => {
        if (nonStopChecked) {
            stopReset()
        } else {
            updateCache(currentData => {
                if (currentData?.filterData?.NewStops?.[selectedJourneyLeg]) {
                    currentData.filterData.NewStops[selectedJourneyLeg].selectedValues = [0]
                }
                return currentData as NonNullable<typeof currentData>
            })
        }
    }, [stopReset, nonStopChecked, updateCache, selectedJourneyLeg])

    const shouldShowReset = useMemo(() => {
        return !!selectedStops?.length
    }, [selectedStops, selectedJourneyLeg])

    return {
        stopOptions,
        stopToggle,
        stopReset,
        nonStopToggle,
        shouldShowReset,
        nonStopChecked,
        title,
        isNonstopFlightAvailable
    }
}

export default useStopFilterManager
